import { useState, useEffect, useRef, useCallback } from "react";
import { customFetch, ApiError } from "@workspace/api-client-react";
import { useAuth } from "@/hooks/use-auth";
import { Loader2, ArrowLeft } from "lucide-react";
import { ELE_CHEGA_EM_30S } from "@/lib/avisoDoCodigo";

// O mesmo intervalo do servidor (CODE_RESEND_COOLDOWN_MS): código novo só depois de 2 min (Pedro, 31/10/2026).
const RESEND_COOLDOWN_SECONDS = 120;
const minutosESegundos = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

export type RequestCodeResult =
  | { status: "sent"; requestId: string; whatsappFormattedNumber: string }
  | { status: "not_found"; whatsappFormattedNumber: string };

export function getApiErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const data = err.data as { error?: { message?: string } } | null;
    if (data?.error?.message) return data.error.message;
  }
  return fallback;
}

// Dispara o envio do código pro WhatsApp. Lança ApiError (ex: número sem
// WhatsApp, 500 no envio) — o caller mostra a mensagem. Retorna "not_found"
// quando não há conta com o número (anti-enumeration: resposta uniforme do
// backend, o frontend usa o flag pra oferecer cadastro).
export async function requestCodeFlow(opts: {
  whatsappNumber: string;
  ddi: string;
  turnstileToken?: string;
  fallbackFormatted?: string | null;
}): Promise<RequestCodeResult> {
  const result = await customFetch<{
    exists?: boolean;
    requestId?: string;
    whatsappFormattedNumber?: string | null;
  }>("/api/auth/request-code", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      whatsappNumber: opts.whatsappNumber,
      ddi: opts.ddi,
      ...(opts.turnstileToken ? { turnstileToken: opts.turnstileToken } : {}),
    }),
  });

  let formatted = result.whatsappFormattedNumber || opts.fallbackFormatted || "";
  if (!formatted) {
    formatted = `+${(opts.ddi + opts.whatsappNumber).replace(/\D/g, "")}`;
  }

  if (result.exists && result.requestId) {
    return { status: "sent", requestId: result.requestId, whatsappFormattedNumber: formatted };
  }
  return { status: "not_found", whatsappFormattedNumber: formatted };
}

interface CodeLoginCardProps {
  ddi: string;
  whatsappNumber: string;
  whatsappFormattedNumber: string;
  requestId: string;
  onBack?: () => void;
}

export function CodeLoginCard({
  ddi,
  whatsappNumber,
  whatsappFormattedNumber,
  requestId: initialRequestId,
  onBack,
}: CodeLoginCardProps) {
  const { loginWithSession } = useAuth();
  const [requestId, setRequestId] = useState(initialRequestId);
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN_SECONDS);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const verify = useCallback(
    async (value: string) => {
      setVerifying(true);
      setError("");
      try {
        const data = await customFetch<{ user: unknown; store: unknown; sessionId: string }>(
          "/api/auth/verify-code",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ requestId, code: value }),
          },
        );
        // loginWithSession semeia o /me e navega pra "/" — a sessão nasce
        // aqui, no navegador real (não na WebView do WhatsApp).
        loginWithSession(data as { user: any; store: any; sessionId: string });
      } catch (err) {
        setError(getApiErrorMessage(err, "Não foi possível validar o código. Tente novamente."));
        setCode("");
        setVerifying(false);
        inputRef.current?.focus();
      }
    },
    [requestId, loginWithSession],
  );

  const handleChange = (raw: string) => {
    const digits = raw.replace(/\D/g, "").slice(0, 4);
    setCode(digits);
    if (error) setError("");
    if (digits.length === 4 && !verifying) {
      void verify(digits);
    }
  };

  const handleResend = async () => {
    if (cooldown > 0 || resending || verifying) return;
    setResending(true);
    setError("");
    try {
      const result = await requestCodeFlow({
        whatsappNumber,
        ddi,
        fallbackFormatted: whatsappFormattedNumber,
      });
      if (result.status === "sent") {
        setRequestId(result.requestId);
        setCode("");
        setCooldown(RESEND_COOLDOWN_SECONDS);
        inputRef.current?.focus();
      } else {
        setError("Não encontramos uma conta com este WhatsApp.");
      }
    } catch (err) {
      setError(getApiErrorMessage(err, "Não foi possível reenviar o código."));
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-right-2 duration-300">
      {onBack && (
        <button
          onClick={onBack}
          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
        >
          <ArrowLeft className="w-4 h-4" /> Voltar
        </button>
      )}

      <div>
        <h2 className="text-3xl font-bold tracking-tight text-foreground">Digite o código</h2>
        <p className="text-muted-foreground mt-2">
          Enviamos um código de 4 dígitos no seu WhatsApp{" "}
          <span className="font-semibold text-foreground">{whatsappFormattedNumber}</span>. {ELE_CHEGA_EM_30S}
        </p>
      </div>

      <div className="space-y-2">
        <input
          ref={inputRef}
          value={code}
          onChange={(e) => handleChange(e.target.value)}
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="[0-9]*"
          maxLength={4}
          disabled={verifying}
          aria-label="Código de acesso"
          className={`w-full h-16 text-center text-4xl font-mono tracking-[0.4em] rounded-xl border-2 bg-background outline-none transition-colors disabled:opacity-60 ${
            error ? "border-red-500 ring-1 ring-red-500" : "border-input focus:border-primary"
          }`}
          placeholder="••••"
        />
        {error && (
          <p className="text-sm text-red-500 font-medium animate-in fade-in slide-in-from-top-1">{error}</p>
        )}
        {verifying && (
          <p className="text-sm text-muted-foreground flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Verificando…
          </p>
        )}
      </div>

      <p className="text-sm text-muted-foreground">
        Não recebeu?{" "}
        <button
          type="button"
          onClick={handleResend}
          disabled={cooldown > 0 || resending || verifying}
          className="text-primary font-semibold hover:underline disabled:opacity-50 disabled:no-underline disabled:cursor-default cursor-pointer"
        >
          {resending ? "Reenviando…" : cooldown > 0 ? `Reenviar em ${minutosESegundos(cooldown)}` : "Reenviar código"}
        </button>
      </p>
    </div>
  );
}
